import {
  InventoryConfigNumberValues,
  InventoryConfigValues,
  VendorRegionAllBagSizesConfig,
  VendorRegionAllGrindTypesConfig,
  VendorRegionMultipleBagConfig,
} from "@shared/index";
import * as T from "@shared/types";

export type UseConfigUtils = {
  findTranslatedValue: (key: keyof T.LocalisedProductConfig, value?: string) => string | undefined;
};

export type TranslationCategoriesMap = { [key in T.TranslationCategory]: T.LocalizedProductConfigKeys };
export type LocaleWithLocalisedProductConfig = { [key in T.Locale]?: T.LocalisedProductConfig };
export type LocaleWithPimProductsCategories = { [key in T.Locale]?: T.PimProductsCategories };

export const configLocalStorageKeys = {
  config: "@config",
};

export interface ConfigState {
  initialized: boolean;
  loading: boolean;
  error: Error | undefined;
  config: RegionConfig;
}

export type ConfigContextState = [state: ConfigState, dispatch: ConfigDispatch, utils?: UseConfigUtils];

export interface RegionConfig {
  // object with product translations from PIM by locale for the region
  localisedProductConfigByLocale: LocaleWithLocalisedProductConfig | undefined;
  // config from Salesforce
  productConfig: T.ProductConfig | undefined;
  // the localised product config based on what locale is set by UI
  selectedLocalisedProductConfig: T.LocalisedProductConfig | undefined;
  // the localised product config based on the region of the vendor
  defaultRegionLocalisedProductConfig: T.LocalisedProductConfig | undefined;
  allBagSizesConfig: VendorRegionAllBagSizesConfig | undefined;
  allGrindTypesConfig: VendorRegionAllGrindTypesConfig | undefined;
  inventoryConfig: InventoryConfigNumberValues | undefined;
  multipleBagConfig: VendorRegionMultipleBagConfig | undefined;
  isMultipleBagEnabled: boolean;
  regionLargeBagSize: string[] | undefined;
  roasterNoteMaxLength: number;
  productDescriptionMaxLength: number;
  titleMaxLength: number;
  sltCustomerAccountNumber?: string; // SLT customer account number from server-side env var
}

export type AllParamsConfig = Omit<
  RegionConfig,
  | "isMultipleBagEnabled"
  | "localisedProductConfigByLocale"
  | "productCategories"
  | "productConfig"
  | "selectedLocalisedProductConfig"
  | "defaultRegionLocalisedProductConfig"
  | "regionLargeBagSize"
  | "roasterNoteMaxLength"
  | "productDescriptionMaxLength"
  | "titleMaxLength"
>;

export interface AllParamsConfigResponse
  extends Omit<
    RegionConfig,
    | "isMultipleBagEnabled"
    | "localisedProductConfigByLocale"
    | "productCategories"
    | "productConfig"
    | "inventoryConfig"
    | "selectedLocalisedProductConfig"
    | "defaultRegionLocalisedProductConfig"
    | "regionLargeBagSize"
    | "roasterNoteMaxLength"
    | "productDescriptionMaxLength"
    | "titleMaxLength"
  > {
  inventoryConfig: InventoryConfigValues;
}

export interface SetLoadingAction {
  type: "SET_LOADING";
  payload: {
    loading: boolean;
  };
}

export interface SetSelectedLocalisedProductConfigAction {
  type: "SET_SELECTED_LOCALISED_PRODUCT_CONFIG";
  payload: {
    selectedLocalisedProductConfig: T.LocalisedProductConfig | undefined;
  };
}

export interface SetDefaultRegionLocalisedProductConfigAction {
  type: "SET_DEFAULT_REGION_LOCALISED_PRODUCT_CONFIG";
  payload: {
    defaultRegionLocalisedProductConfig: T.LocalisedProductConfig | undefined;
  };
}

export interface SetLocalisedProductConfigByLocaleAction {
  type: "SET_LOCALISED_PRODUCT_CONFIG_BY_LOCALE";
  payload: {
    localisedProductConfigByLocale: LocaleWithLocalisedProductConfig;
  };
}

export interface SetInitializedAction {
  type: "SET_INITIALIZED";
  payload: {
    initialized: boolean;
  };
}

export interface SetPimProductCategoriesAction {
  type: "SET_PIM_PRODUCT_CATEGORIES";
  payload: {
    productCategories: T.PimProductsCategories[] | undefined;
  };
}

export interface SetLocalisedProductConfigByLocale {
  type: "SET_LOCALISED_PRODUCT_CONFIG_BY_LOCALE";
  payload: {
    localisedProductConfigByLocale: LocaleWithLocalisedProductConfig;
  };
}

export interface SetAllParameterConfigAction {
  type: "SET_ALL_PARAMETER_CONFIG";
  payload: AllParamsConfig;
}
export interface SetProductConfigAction {
  type: "SET_PRODUCT_CONFIG";
  payload: {
    productConfig: T.ProductConfig | undefined;
  };
}

export interface SetIsMultiBagEnabledAction {
  type: "SET_IS_MULTI_BAG_ENABLED";
  payload: {
    isMultipleBagEnabled: boolean;
  };
}

export interface SetRegionLargeBagSizeAction {
  type: "SET_REGION_LARGE_BAG_SIZE";
  payload: {
    regionLargeBagSize: string[] | undefined;
  };
}

export type ConfigAction =
  | SetInitializedAction
  | SetPimProductCategoriesAction
  | SetLoadingAction
  | SetIsMultiBagEnabledAction
  | SetProductConfigAction
  | SetAllParameterConfigAction
  | SetLocalisedProductConfigByLocaleAction
  | SetSelectedLocalisedProductConfigAction
  | SetRegionLargeBagSizeAction
  | SetDefaultRegionLocalisedProductConfigAction;

export type ConfigDispatch = React.Dispatch<ConfigAction>;
